"use client";

/* eslint-disable @next/next/no-img-element -- Cart-owned private previews use the session-bound proxy and cannot use the Next image optimizer. */

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import type { ChangeEvent, ReactNode } from "react";
import type { ShopStorefrontMessage } from "./shopStorefrontCore";
import {
    canonicalManufacturingSpecification,
    createPublicManufacturingDesignMediaFormData,
    manufacturingCustomizationContextKey,
    manufacturingSpecificationEntries,
    publicManufacturingDesignMediaContentUrl,
    publicManufacturingDesignMediaFromValue,
    publicManufacturingProfileFromValue,
    publicManufacturingProfileUrl,
    validateManufacturingSpecification,
} from "./shopManufacturingCustomization";
import type {
    ManufacturingSpecificationKey,
    PublicManufacturingProfile,
    PublicManufacturingSpecification,
} from "./shopManufacturingCustomization";

const EMPTY_PROFILE: PublicManufacturingProfile = {
    profileAvailable: false,
    allowedKeys: [],
    requiredKeys: [],
};

const MAX_DESIGN_FILE_SIZE = 10 * 1024 * 1024;
const DESIGN_FILE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

type ShopManufacturingApiContextValue = {
    cartApiBasePath: string | null;
    manufacturingProfileApiBasePath: string | null;
};

const ShopManufacturingApiContext = createContext<ShopManufacturingApiContextValue>({
    cartApiBasePath: null,
    manufacturingProfileApiBasePath: null,
});

export function ShopManufacturingApiProvider({
    cartApiBasePath,
    children,
    manufacturingProfileApiBasePath,
}: {
    cartApiBasePath?: string;
    children: ReactNode;
    manufacturingProfileApiBasePath?: string;
}) {
    const value = useMemo<ShopManufacturingApiContextValue>(() => ({
        cartApiBasePath: cartApiBasePath?.replace(/\/+$/, "") || null,
        manufacturingProfileApiBasePath: manufacturingProfileApiBasePath?.replace(/\/+$/, "") || null,
    }), [cartApiBasePath, manufacturingProfileApiBasePath]);

    return (
        <ShopManufacturingApiContext.Provider value={value}>
            {children}
        </ShopManufacturingApiContext.Provider>
    );
}

type ManufacturingCustomizationError =
    | "fileTooLarge"
    | "profileLoad"
    | "unsupportedFile"
    | "upload";

export type ManufacturingCustomizationController = {
    canSubmit: boolean;
    clearDesign: () => void;
    error: ManufacturingCustomizationError | null;
    loading: boolean;
    profile: PublicManufacturingProfile;
    retryProfile: () => void;
    setValue: (key: Exclude<ManufacturingSpecificationKey, "designMediaUuid">, value: string) => void;
    specification: PublicManufacturingSpecification;
    uploadDesign: (file: File) => Promise<void>;
    uploadingDesign: boolean;
    validation: ReturnType<typeof validateManufacturingSpecification>;
    values: PublicManufacturingSpecification;
};

export function useManufacturingCustomization(options: {
    editorMode: boolean;
    modifierOptionUuids: readonly string[];
    productUuid: string;
    variantUuid: string | null;
}): ManufacturingCustomizationController {
    const api = useContext(ShopManufacturingApiContext);
    const normalizedModifierOptionUuidsKey = JSON.stringify([
        ...new Set(options.modifierOptionUuids.map((value) => value.trim()).filter((value) => value !== "")),
    ]);
    const normalizedModifierOptionUuids = useMemo(
        () => JSON.parse(normalizedModifierOptionUuidsKey) as string[],
        [normalizedModifierOptionUuidsKey],
    );
    const contextKey = manufacturingCustomizationContextKey(
        options.productUuid,
        options.variantUuid,
        normalizedModifierOptionUuids,
    );
    const profileUrl = api.manufacturingProfileApiBasePath === null || options.productUuid.trim() === ""
        ? null
        : publicManufacturingProfileUrl(
            api.manufacturingProfileApiBasePath,
            options.productUuid,
            options.variantUuid,
            normalizedModifierOptionUuids,
        );
    const shouldResolve = !options.editorMode && profileUrl !== null;
    const [profile, setProfile] = useState<PublicManufacturingProfile>(EMPTY_PROFILE);
    const [resolvedContextKey, setResolvedContextKey] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<ManufacturingCustomizationError | null>(null);
    const [retryVersion, setRetryVersion] = useState(0);
    const [values, setValues] = useState<PublicManufacturingSpecification>({});
    const [uploadingDesign, setUploadingDesign] = useState(false);
    const currentContextKey = useRef(contextKey);
    const uploadRequestId = useRef(0);

    currentContextKey.current = contextKey;

    useEffect(() => {
        uploadRequestId.current += 1;
        setUploadingDesign(false);
        setValues((current) => {
            if (current.designMediaUuid === undefined) {
                return current;
            }

            const next = { ...current };
            delete next.designMediaUuid;

            return next;
        });
        setError(null);
    }, [contextKey]);

    useEffect(() => {
        if (!shouldResolve || profileUrl === null) {
            setProfile(EMPTY_PROFILE);
            setResolvedContextKey(contextKey);
            setLoading(false);

            return;
        }

        const abortController = new AbortController();

        setLoading(true);
        setError(null);
        setResolvedContextKey(null);

        void (async () => {
            try {
                const response = await fetch(profileUrl, {
                    credentials: "same-origin",
                    cache: "no-store",
                    signal: abortController.signal,
                });

                if (!response.ok) {
                    throw new Error("profile");
                }

                const nextProfile = publicManufacturingProfileFromValue(await response.json());

                if (nextProfile === null) {
                    throw new Error("profile");
                }

                setProfile(nextProfile);
                setValues((current) => canonicalManufacturingSpecification(nextProfile, current));
                setResolvedContextKey(contextKey);
            } catch {
                if (!abortController.signal.aborted) {
                    setProfile(EMPTY_PROFILE);
                    setError("profileLoad");
                    setResolvedContextKey(contextKey);
                }
            } finally {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                }
            }
        })();

        return () => abortController.abort();
    }, [contextKey, profileUrl, retryVersion, shouldResolve]);

    const setValue = useCallback((key: Exclude<ManufacturingSpecificationKey, "designMediaUuid">, value: string) => {
        setValues((current) => ({ ...current, [key]: value }));
    }, []);

    const clearDesign = useCallback(() => {
        setValues((current) => {
            const next = { ...current };
            delete next.designMediaUuid;

            return next;
        });
        setError(null);
    }, []);

    const uploadDesign = useCallback(async (file: File): Promise<void> => {
        if (!DESIGN_FILE_TYPES.has(file.type)) {
            setError("unsupportedFile");

            return;
        }

        if (file.size < 1 || file.size > MAX_DESIGN_FILE_SIZE) {
            setError("fileTooLarge");

            return;
        }

        if (api.cartApiBasePath === null || options.productUuid.trim() === "") {
            setError("upload");

            return;
        }

        const uploadContextKey = contextKey;
        const requestId = uploadRequestId.current + 1;
        uploadRequestId.current = requestId;
        const formData = createPublicManufacturingDesignMediaFormData({
            file,
            fileName: file.name,
            productUuid: options.productUuid,
            variantUuid: options.variantUuid,
            modifierOptionUuids: normalizedModifierOptionUuids,
        });

        setUploadingDesign(true);
        setError(null);

        try {
            const response = await fetch(`${api.cartApiBasePath}/manufacturing-design-media`, {
                method: "POST",
                credentials: "same-origin",
                cache: "no-store",
                body: formData,
            });

            if (!response.ok) {
                throw new Error("upload");
            }

            const uploaded = publicManufacturingDesignMediaFromValue(await response.json());

            if (currentContextKey.current !== uploadContextKey || uploadRequestId.current !== requestId) {
                return;
            }

            if (uploaded === null) {
                throw new Error("upload");
            }

            setValues((current) => ({ ...current, designMediaUuid: uploaded.designMediaUuid }));
        } catch {
            if (currentContextKey.current === uploadContextKey && uploadRequestId.current === requestId) {
                setError("upload");
            }
        } finally {
            if (uploadRequestId.current === requestId) {
                setUploadingDesign(false);
            }
        }
    }, [api.cartApiBasePath, contextKey, normalizedModifierOptionUuids, options.productUuid, options.variantUuid]);

    const validation = useMemo(
        () => validateManufacturingSpecification(profile, values),
        [profile, values],
    );
    const specification = useMemo(
        () => canonicalManufacturingSpecification(profile, values),
        [profile, values],
    );
    const resolvingCurrentProfile = shouldResolve
        && (loading || resolvedContextKey !== contextKey);

    return {
        canSubmit: !resolvingCurrentProfile
            && error !== "profileLoad"
            && !uploadingDesign
            && validation.valid,
        clearDesign,
        error,
        loading: resolvingCurrentProfile,
        profile,
        retryProfile: () => setRetryVersion((current) => current + 1),
        setValue,
        specification,
        uploadDesign,
        uploadingDesign,
        validation,
        values,
    };
}

function fieldLabel(
    key: ManufacturingSpecificationKey,
    message: ShopStorefrontMessage,
): string {
    if (key === "color") {
        return message("ui.manufacturing.color");
    }

    if (key === "size") {
        return message("ui.manufacturing.size");
    }

    if (key === "text") {
        return message("ui.manufacturing.text");
    }

    return message("ui.manufacturing.design");
}

function customizationErrorText(
    error: ManufacturingCustomizationError,
    message: ShopStorefrontMessage,
): string {
    if (error === "fileTooLarge") {
        return message("ui.manufacturing.fileTooLarge");
    }

    if (error === "unsupportedFile") {
        return message("ui.manufacturing.unsupportedFile");
    }

    if (error === "profileLoad") {
        return message("ui.manufacturing.profileLoadError");
    }

    return message("ui.manufacturing.uploadError");
}

export function ManufacturingCustomizationFields({
    appearance,
    controller,
    message,
}: {
    appearance: "laguna" | "morrow";
    controller: ManufacturingCustomizationController;
    message: ShopStorefrontMessage;
}) {
    const api = useContext(ShopManufacturingApiContext);
    const rootClassName = appearance === "morrow"
        ? "mt-6 border-t border-[var(--site-border)] pt-5"
        : "mt-6 rounded-3xl border border-[var(--site-border)] bg-[color-mix(in_srgb,var(--site-surface)_80%,transparent)] p-4";
    const fieldClassName = appearance === "morrow"
        ? "grid gap-2 text-sm font-bold"
        : "grid gap-2 text-sm font-black";
    const inputClassName = appearance === "morrow"
        ? "min-h-11 w-full rounded-xl border border-[var(--site-border)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--morrow-green)]"
        : "min-h-11 w-full rounded-xl border border-[var(--site-border)] bg-[var(--site-surface)] px-3 py-2 text-sm font-semibold outline-none focus:border-[var(--site-primary)]";

    if (controller.loading) {
        return <p className={rootClassName} role="status">{message("ui.manufacturing.loading")}</p>;
    }

    if (controller.error === "profileLoad") {
        return (
            <div className={rootClassName} role="alert">
                <p>{customizationErrorText(controller.error, message)}</p>
                <button className={appearance === "morrow" ? "mt-3 rounded-xl border border-[var(--site-border)] bg-white px-4 py-2 text-sm font-bold" : "mt-3 rounded-full border border-[var(--site-border)] px-4 py-2 text-sm font-black"} onClick={controller.retryProfile} type="button">
                    {message("ui.manufacturing.retry")}
                </button>
            </div>
        );
    }

    if (!controller.profile.profileAvailable || controller.profile.allowedKeys.length === 0) {
        return null;
    }

    const designPreviewUrl = controller.values.designMediaUuid && api.cartApiBasePath
        ? publicManufacturingDesignMediaContentUrl(api.cartApiBasePath, controller.values.designMediaUuid)
        : null;
    const textError = controller.error === null
        ? null
        : customizationErrorText(controller.error, message);

    async function handleDesignChange(event: ChangeEvent<HTMLInputElement>): Promise<void> {
        const file = event.currentTarget.files?.[0];
        event.currentTarget.value = "";

        if (file) {
            await controller.uploadDesign(file);
        }
    }

    return (
        <fieldset className={rootClassName}>
            <legend>{message("ui.manufacturing.title")}</legend>
            <p className={appearance === "morrow" ? "mt-1 text-xs text-[var(--site-muted)]" : "mt-1 text-xs font-semibold text-[color-mix(in_srgb,var(--site-text)_55%,transparent)]"}>
                {message("ui.manufacturing.description")}
            </p>
            <div className="mt-4 grid gap-4">
                {controller.profile.allowedKeys.includes("color") ? (
                    <label className={fieldClassName}>
                        <span>{fieldLabel("color", message)}{controller.profile.requiredKeys.includes("color") ? " *" : ""}</span>
                        <input
                            className={inputClassName}
                            maxLength={100}
                            onChange={(event) => controller.setValue("color", event.currentTarget.value)}
                            required={controller.profile.requiredKeys.includes("color")}
                            type="text"
                            value={controller.values.color ?? ""}
                        />
                    </label>
                ) : null}
                {controller.profile.allowedKeys.includes("size") ? (
                    <label className={fieldClassName}>
                        <span>{fieldLabel("size", message)}{controller.profile.requiredKeys.includes("size") ? " *" : ""}</span>
                        <input
                            className={inputClassName}
                            maxLength={100}
                            onChange={(event) => controller.setValue("size", event.currentTarget.value)}
                            required={controller.profile.requiredKeys.includes("size")}
                            type="text"
                            value={controller.values.size ?? ""}
                        />
                    </label>
                ) : null}
                {controller.profile.allowedKeys.includes("text") ? (
                    <label className={fieldClassName}>
                        <span>{fieldLabel("text", message)}{controller.profile.requiredKeys.includes("text") ? " *" : ""}</span>
                        <textarea
                            className={inputClassName}
                            maxLength={1000}
                            onChange={(event) => controller.setValue("text", event.currentTarget.value)}
                            required={controller.profile.requiredKeys.includes("text")}
                            rows={3}
                            value={controller.values.text ?? ""}
                        />
                    </label>
                ) : null}
                {controller.profile.allowedKeys.includes("designMediaUuid") ? (
                    <div className={fieldClassName}>
                        <span>{fieldLabel("designMediaUuid", message)}{controller.profile.requiredKeys.includes("designMediaUuid") ? " *" : ""}</span>
                        <label className={appearance === "morrow" ? "flex cursor-pointer items-center justify-center rounded-xl border border-dashed border-[var(--site-border)] bg-white px-4 py-3 text-center text-sm font-bold hover:border-[var(--morrow-green)]" : "flex cursor-pointer items-center justify-center rounded-xl border border-dashed border-[var(--site-border)] bg-[var(--site-surface)] px-4 py-3 text-center text-sm font-black hover:border-[var(--site-primary)]"}>
                            <span>{controller.uploadingDesign ? message("ui.manufacturing.uploading") : message("ui.manufacturing.chooseFile")}</span>
                            <input
                                accept="image/jpeg,image/png,image/webp"
                                aria-label={message("ui.manufacturing.chooseFile")}
                                className="sr-only"
                                disabled={controller.uploadingDesign}
                                onChange={(event) => void handleDesignChange(event)}
                                type="file"
                            />
                        </label>
                        <small>{message("ui.manufacturing.fileHelp")}</small>
                        {designPreviewUrl ? (
                            <div className={appearance === "morrow" ? "flex items-center gap-3 rounded-xl bg-white p-3" : "flex items-center gap-3 rounded-2xl bg-[var(--site-surface)] p-3"}>
                                <img alt={message("ui.manufacturing.designPreviewAlt")} className="h-20 w-20 rounded-xl object-cover" src={designPreviewUrl}/>
                                <button className={appearance === "morrow" ? "rounded-xl border border-[var(--site-border)] px-4 py-2 text-xs font-bold" : "rounded-full border border-[var(--site-border)] px-4 py-2 text-xs font-black"} onClick={controller.clearDesign} type="button">
                                    {message("ui.manufacturing.removeDesign")}
                                </button>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>
            {textError ? <p className={appearance === "morrow" ? "morrow-error" : "mt-3 rounded-xl bg-[var(--site-primary-soft)] px-3 py-2 text-sm font-bold text-[var(--site-primary)]"} role="alert">{textError}</p> : null}
            {controller.validation.missingKeys.length > 0 ? <p className={appearance === "morrow" ? "morrow-error" : "mt-3 rounded-xl bg-[var(--site-primary-soft)] px-3 py-2 text-sm font-bold text-[var(--site-primary)]"}>{message("ui.manufacturing.requiredFields")}</p> : null}
            {controller.validation.invalidKeys.length > 0 ? <p className={appearance === "morrow" ? "morrow-error" : "mt-3 rounded-xl bg-[var(--site-primary-soft)] px-3 py-2 text-sm font-bold text-[var(--site-primary)]"}>{message("ui.manufacturing.invalidFields")}</p> : null}
        </fieldset>
    );
}

export function ManufacturingSpecificationSummary({
    appearance,
    compact = false,
    message,
    specification,
}: {
    appearance: "laguna" | "morrow";
    compact?: boolean;
    message: ShopStorefrontMessage;
    specification: unknown;
}) {
    const api = useContext(ShopManufacturingApiContext);
    const entries = manufacturingSpecificationEntries(specification);

    if (entries.length === 0) {
        return null;
    }

    const designMediaUuid = entries.find(([key]) => key === "designMediaUuid")?.[1] ?? null;
    const textEntries = entries.filter(([key]) => key !== "designMediaUuid");
    const previewUrl = designMediaUuid !== null && api.cartApiBasePath !== null
        ? publicManufacturingDesignMediaContentUrl(api.cartApiBasePath, designMediaUuid)
        : null;
    const className = appearance === "morrow"
        ? compact
            ? "mt-1 grid gap-1 text-xs text-[var(--site-muted)]"
            : "mt-2 grid gap-2 rounded-xl bg-white p-3 text-xs text-[var(--site-muted)]"
        : compact
            ? "mt-1 grid gap-1 text-xs font-semibold text-[color-mix(in_srgb,var(--site-text)_50%,transparent)]"
            : "mt-2 grid gap-2 rounded-2xl bg-[color-mix(in_srgb,var(--site-surface)_65%,transparent)] p-3 text-xs font-semibold text-[color-mix(in_srgb,var(--site-text)_60%,transparent)]";

    return (
        <div className={className}>
            <strong>{message("ui.manufacturing.summaryTitle")}</strong>
            {textEntries.map(([key, value]) => (
                <span key={key}><b>{fieldLabel(key, message)}:</b> {value}</span>
            ))}
            {designMediaUuid !== null ? (
                <div className="flex items-center gap-2">
                    {previewUrl ? <img alt={message("ui.manufacturing.designPreviewAlt")} className={compact ? "h-10 w-10 rounded-lg object-cover" : "h-16 w-16 rounded-xl object-cover"} src={previewUrl}/> : null}
                    <span>{message("ui.manufacturing.designAttached")}</span>
                </div>
            ) : null}
        </div>
    );
}
